/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { SageContextSource } from "@tivocorp/sage-web-sdk/api";
import { LogType } from "@tivocorp/sage-web-sdk/api";

import type { DeviceModel } from "../device";

export interface DeviceContextProvider {
  getAppName(): string;
  getAppVersionName(): string;
  getDeviceModel(): string;
  getDeviceType(): string;
  getPlatformName(): string;
  getPlatformVersion(): string;
  getDateLocale(): string;
  getActiveConnectionType(): string;
  getProductName(): string;
  getAccountId(): string;
  getRegion(): string;
  getHsn(): string;
  getLocalIp(): string;
  getManagedIp(): string;
  getLoggingVersion(): string;
  getProfileId(): string;
  getDeviceName(): string;
  getAppLanguage(): string;
}

/** Strips non-numeric prefixes (e.g. "development-v") and drops the last dot segment, e.g. "10.15.7" -> "10.15". */
function simplifyVersion(version: string): string {
  const firstDigitIndex = version.search(/\d/);
  const numeric =
    firstDigitIndex === -1 ? version : version.slice(firstDigitIndex);
  const parts = numeric.split(".");
  return parts.length <= 1 ? numeric : parts.slice(0, -1).join(".");
}

/** Only macOS needs re-casing; supported platforms are browsers, webOS, and Tizen. */
function buildPlatformDetail(platformName: string): string {
  return platformName === "macOS" ? "MacOS" : platformName;
}

export class DeviceModelSageContextSource implements SageContextSource {
  constructor(private readonly getDevice: () => DeviceContextProvider) {}

  getSnapshot(_logType: LogType): Record<string, string> {
    const device = this.getDevice();
    const platformName = device.getPlatformName();
    const platformVersion = device.getPlatformVersion();
    const appVersion = device.getAppVersionName();
    return {
      appName: device.getAppName(),
      appVersion,
      deviceModel: device.getDeviceModel(),
      deviceType: device.getDeviceType(),
      platformName,
      platformVersion,
      deviceLanguage: device.getDateLocale(),
      networkType: device.getActiveConnectionType(),
      productName: device.getProductName(),
      timestamp: String(Date.now()),
      account_id: device.getAccountId(),
      region: device.getRegion(),
      hsn: device.getHsn(),
      local_ip: device.getLocalIp(),
      managed_ip: device.getManagedIp(),
      platform_version_simple: simplifyVersion(platformVersion),
      app_version_simple: simplifyVersion(appVersion),
      logging_version: device.getLoggingVersion(),
      profile_id: device.getProfileId(),
      device: device.getDeviceName(),
      platform_detail: buildPlatformDetail(platformName),
      app_language: device.getAppLanguage(),
      country: device.getRegion()
    };
  }

  static fromDeviceModel(
    deviceModel: typeof DeviceModel
  ): DeviceModelSageContextSource {
    return new DeviceModelSageContextSource(() => {
      const tvProps = deviceModel.getTVProperties();
      const tivoSettings = deviceModel.getTiVoSettings();
      return {
        getAppName: () => deviceModel.getAppName(),
        getAppVersionName: () => deviceModel.getAppVersionName(),
        getDeviceModel: () => tvProps.getDeviceModel(),
        getDeviceType: () => tivoSettings.getDeviceType(),
        getPlatformName: () => tvProps.getPlatformName(),
        getPlatformVersion: () => tvProps.getPlatformVersion(),
        getDateLocale: () => tvProps.getDateLocale(),
        getActiveConnectionType: () => tvProps.getActiveConnectionType(),
        // Web has no distinct product name — reuse appName, matching CommonHeaders/DrmManager convention.
        getProductName: () => deviceModel.getAppName(),
        getAccountId: () => deviceModel.getAccountId(),
        getRegion: () => tvProps.getRegion(),
        getHsn: () => tvProps.getHsn(),
        getLocalIp: () => tvProps.getIpAddress(),
        getManagedIp: () => deviceModel.getWanIpAddress(),
        getLoggingVersion: () => deviceModel.SAGE_LOGGING_VERSION,
        getProfileId: () => tivoSettings.getProfileId(),
        getDeviceName: () => tvProps.getDeviceName(),
        getAppLanguage: () => tvProps.getLocale()
      };
    });
  }
}
